import { retrieveProductList } from '@/api/product'
import { buriedPointUtil, gaAnalytics, metaPixelTrack } from '@/utils/utils'
import { createSharedComposable } from '@vueuse/core'
import { useListProcess } from './useListProcess'
import { useDevice } from '@/hooks/useDevice'
import { useTextSearch } from '@/hooks/useTextSearch'
import { useLoginModal } from '@/components/Login'
import { useLanguageStore } from '@/pinia/language'
import { useCurrencyStore } from '@/pinia/currency'

/**
 * @returns {object}
 * filter - 返回过滤项配置
 * loading - 当前是否处于请求列表数据中
 * list - 返回列表数据
 * onLoad - 加载更多
 * onReset - 重置 page / loading / finished 状态并触发 onLoad 事件
 */
export const useProductList = createSharedComposable(() => {
  const route = useRoute()
  const { list, handleRawList } = useListProcess()
  const { deviceType } = useDevice()
  const { insertTextSearchRecord } = useTextSearch()
  const { show: showLoginModal, open: openLoginModal } = useLoginModal()
  const language = useLanguageStore()
  const currency = useCurrencyStore()

  const loading = ref(false)
  const finished = ref(false)

  const filter = reactive({
    keyword: route.query.keyword,
    categoryId: route.query.categoryId,
    language: language.current,
    sortMap: null
  })
  /**
   * 存储已翻译后的 keyword，来减少 keyword 翻译次数
   */
  const translatedKeyword = reactive({
    source: filter.keyword,
    /**
     * Ai Search 会提前获取到翻译后的 keyword，这样就不要再翻译关键词了
     */
    target: route.query.keywordCn ?? (filter.language === 'zh' ? filter.keyword : null)
  })
  const page = reactive({
    pageNow: 1,
    pageSize: 50
  })

  /**
   * 防止请求竞态导致列表数据展示不正确问题
   */
  let latestRequestId = 0

  const onLoad = async () => {
    /**
     * 当出现登录弹窗时，不再触发 onLoad
     */
    if (showLoginModal.value) return
    if (loading.value || finished.value) return
    const currentRequestId = ++latestRequestId
    const data = { ...filter, ...page }
    loading.value = true
    try {
      /**
       * 只要是第一页都调用 searchKeyWord async：true 接口来保证服务端能缓存 5 页数据
       * 该接口可以顺便获取到翻译后的 keyword，及时保存即可
       */
      if (page.pageNow === 1) {
        const { keyWordCn } = await translateKeyword(data)
        if (data.keyword !== translatedKeyword.source || !translatedKeyword.target) {
          translatedKeyword.source = filter.keyword
          translatedKeyword.target = keyWordCn
        }
      }
      data.keyword = translatedKeyword.target
      data.language = 'zh'
      if (data.priceStart) {
        data.priceStart = data.priceStart / currency.current.rate
      }
      if (data.priceStart) {
        data.priceEnd = data.priceEnd / currency.current.rate
      }
      const rows = await getProductList(data)
      if (currentRequestId === latestRequestId) {
        if (rows.length > 0) {
          list.value = [...list.value, ...handleRawList(rows)]
          loading.value = false
          page.pageNow++
        } else {
          finished.value = true
        }
      }
    } catch (e) {
      console.error(e)
      loading.value = false
    }
  }

  const translateKeyword = async (filter) => {
    const { code, data, msg } = await retrieveProductList({
      ...filter,
      async: true
    })
    if (code === 200) {
      return {
        keyWordCn: data.keyWordCn
      }
    } else {
      throw new Error(msg)
    }
  }

  const getProductList = async (params) => {
    const { code, data, msg } = await retrieveProductList({
      ...params,
      async: false
    }, {
      failedRedirect: params.pageNow === 1,
      manualHandle401Error: params.pageNow > 1
    })
    if (deviceType.value === 'MOBILE') {
      buriedPointUtil({ eventId: 'MPSC201' })
      gaAnalytics('mp_search_page_view', 'mp_search_page_view')
      if (code === 401) {
        buriedPointUtil({ eventId: 'MPLG501' })
      }
      if (params.pageNow > 1) {
        buriedPointUtil({ eventId: 'MPSC401', others: { ...params } })
      }
    } else {
      if (params.pageNow > 1) {
        buriedPointUtil({ eventId: 'PSC401', others: { ...params } })
        gaAnalytics('p_search_page_load_more', 'p_search_page_load_more')
      }
    }
    if (code === 401 && params.pageNow > 1) {
      openLoginModal()
    }
    if (code !== 200) {
      throw new Error(msg)
    }
    /**
     * Meta Pixel 埋点：全局搜索(含类目搜索)触发
     */
    if (params.pageNow === 1 && params.keyword != null && params.keyword !== '') {
      metaPixelTrack('PSC401', 'Search', {
        content_ids: data.rows.slice(0, 10).map((item) => item.offerId),
        content_type: 'product_group',
        currency: '$',
        value: 0.1,
        search_string: filter.keyword
      })
    }
    return data.rows
  }

  onBeforeMount(() => {
    onLoad()
  })

  watch(() => filter.keyword, () => {
    if (filter.keyword) {
      insertTextSearchRecord(filter.keyword)
    }
  }, {
    immediate: true
  })

  const onReset = () => {
    page.pageNow = 1
    list.value = []
    loading.value = false
    finished.value = false
    onLoad()
  }

  return { filter, loading, list, onLoad, onReset }
})
